/**
 * Which scored dimensions the Fit Score is actually built from, and how to
 * label them for a reader.
 *
 * WHY THIS EXISTS. The candidate scorecard renders
 * `Object.entries(candidate.dimensions)` — every key the API returns, each with
 * a `score/5` progress bar. Nothing filters at any layer: not the deep-matching
 * pipeline, not avrio-backend, not this app. So a stored record carrying
 * `compensationFit` or `timingStability` renders them exactly like the eight
 * that count, which tells a hiring team those dimensions moved the number.
 *
 * They do not. `agents/interview/rubric.py` holds
 * `UNSCORED_DIMENSIONS = {"compensationFit", "timingStability"}`, and PRD v4.2
 * ledger C10 is explicit that logistics answers "raise flags and never affect
 * the Fit Score; salary, location and notice are negotiable".
 *
 * `compensationFit` left scoring on 2026-08-27. `timingStability` joined it in
 * avrio-rio #79 — it had been carrying weight 1.0 against a 12.5 total, 8% of
 * every Fit Score, while the PRD promised it counted for nothing. Every
 * historical record still carries both.
 *
 * SO THEY ARE SHOWN, NOT HIDDEN. Hiding them would lose the flag C10 says to
 * raise; showing them with a bar asserts a score the product denies. They get
 * their own group, their justification, and no number.
 */

/**
 * Mirrors `UNSCORED_DIMENSIONS` in avrio-rio's agents/interview/rubric.py.
 *
 * Duplicated across a language boundary, so it can drift. It is deliberately
 * NOT a filter of "dimensions we recognise" — an unknown key stays scored and
 * visible, because a new dimension the pipeline starts sending should appear
 * rather than vanish into a stale allowlist here.
 */
export const UNSCORED_DIMENSIONS: ReadonlySet<string> = new Set([
  'compensationFit',
  'timingStability',
  // Removed from scoring 2026-09-29 per Nachi - still shown with its own
  // justification, just no longer carries a Fit Score weight. Same
  // treatment as the two above.
  'culturalFit',
]);

export const isScoredDimension = (key: string): boolean =>
  !UNSCORED_DIMENSIONS.has(key);

/**
 * `workContent` -> `Work content`.
 *
 * The previous rendering was `key.replace(/([A-Z])/g, ' $1')` under a CSS
 * `text-transform: capitalize`, which produced "work Content" and "role
 * Progression" — the camel hump capitalised, the first letter not, because
 * `capitalize` acts per word and the split had already made two. The design
 * system specifies sentence case everywhere.
 */
export const dimensionLabel = (key: string): string => {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * `explanatoryNote` is generated text (avrio-backend reads it verbatim from
 * DynamoDB's `explanatory_notes`), and the generation prompt interpolates
 * raw dimension keys straight into it -- "Exceptional workContent and
 * adaptability..." reaches a hiring manager as literal camelCase.
 *
 * The real fix is upstream, in whatever writes explanatory_notes. This is
 * the frontend mitigation: scan the note for the CURRENT candidate's own
 * dimension keys (not a hardcoded list -- this file's own philosophy above
 * is "no stale allowlist") and swap each for `dimensionLabel`. A key with no
 * word-boundary match (already phrased some other way, or not present)
 * is simply not replaced.
 */
export const humanizeGeneratedText = (
  text: string,
  dimensionKeys: string[],
): string =>
  dimensionKeys.reduce(
    (result, key) =>
      result.replace(new RegExp(`\\b${key}\\b`, 'g'), dimensionLabel(key)),
    text,
  );
